/**
 * SimpleDropdown — minimal "button + popup list" dropdown used by the
 * Ads Manager bulk-action toolbar (Actions / Rules), and ColumnsModal's
 * preset switcher. No portals, click-outside auto-close, keyboard
 * dismiss with Escape.
 *
 * Items render as plain buttons in the popup. The caller decides
 * what each item does via `onPick(item.value)`. Items with
 * `disabled: true` render dimmed and don't fire onPick.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";

export type SimpleDropdownItem<V extends string = string> = {
  value: V;
  label: string;
  disabled?: boolean;
  /** Optional separator BEFORE this item (renders a thin divider). */
  separatorAbove?: boolean;
};

export function SimpleDropdown<V extends string = string>({
  label,
  items,
  onPick,
  disabled = false,
  align = "right",
  width = 200,
  triggerClassName = "btn sm",
  triggerContent,
  emptyMessage,
  footer,
}: {
  label: string;
  items: SimpleDropdownItem<V>[];
  onPick: (value: V) => void;
  disabled?: boolean;
  align?: "left" | "right";
  width?: number;
  triggerClassName?: string;
  /** Override the trigger's inner content (defaults to "label ▾"). */
  triggerContent?: ReactNode;
  /** Shown above footer when items is empty (e.g. "no presets"). */
  emptyMessage?: string;
  /** Always-visible row at the bottom (e.g. "Edit columns"). */
  footer?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayer(open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        {triggerContent ?? (
          <>
            <span>{label}</span>
            {I.chevronDown}
          </>
        )}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            [align]: 0,
            minWidth: width,
            background: "var(--c-surface, white)",
            border: "1px solid var(--c-border, #ddd)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            zIndex: 50,
            padding: 4,
          }}
        >
          {items.length === 0 && emptyMessage && (
            <div
              style={{
                padding: "8px 10px",
                fontSize: 12,
                color: "var(--c-text-3, #777)",
              }}
            >
              {emptyMessage}
            </div>
          )}
          {items.map((it, idx) => (
            <div key={`${it.value}-${idx}`}>
              {it.separatorAbove && (
                <div
                  style={{
                    borderTop: "1px solid var(--c-divider, #eee)",
                    margin: "4px 0",
                  }}
                />
              )}
              <button
                type="button"
                disabled={it.disabled}
                onClick={() => {
                  if (it.disabled) return;
                  onPick(it.value);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "6px 10px",
                  background: "transparent",
                  border: 0,
                  cursor: it.disabled ? "not-allowed" : "pointer",
                  fontSize: 13,
                  color: it.disabled
                    ? "var(--c-text-3, #999)"
                    : "var(--c-text, #222)",
                  borderRadius: 6,
                }}
                onMouseEnter={(e) => {
                  if (!it.disabled)
                    (e.currentTarget.style.background =
                      "var(--c-surface-alt, #f5f5f5)");
                }}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                {it.label}
              </button>
            </div>
          ))}
          {footer && (
            <>
              <div
                style={{
                  borderTop: "1px solid var(--c-divider, #eee)",
                  margin: "4px 0",
                }}
              />
              {footer}
            </>
          )}
        </div>
      )}
    </div>
  );
}
